import http, {
  postData,
  postFormData,
  getData,
  patchData,
  setAuthorizationHeader,
  postDataFetch,
  getDataFetch,
} from "./http.js";
import endpoint from "./endpoint.js";

import _local from "utils/localStorageUtils";

// Users Authorization
const auth = {
  checkAppVersion: (appVersion, isAppData) =>
    !!appVersion &&
    postData({
      endpoint: endpoint?.auth?.checkVersion(appVersion),
      body: { isApp: isAppData },
    }),
  forgotPassword: data =>
    !!data &&
    postData({
      endpoint: endpoint?.auth?.forgotPassword,
      body: data,
    }),
  resetPassword: data =>
    !!data &&
    postData({
      endpoint: endpoint?.auth?.resetPassword,
      body: data,
    }),
  confirmProfile: data =>
    !!data &&
    postData({
      endpoint: endpoint?.auth?.confirmProfile,
      body: data,
    }),
  resendConfirmProfile: data =>
    !!data &&
    postData({
      endpoint: endpoint?.auth?.confirmProfileResend,
      body: data,
    }),
  signUp: data =>
    !!data &&
    postData({
      endpoint: endpoint?.auth?.signUp,
      body: data,
    }),
  registerToken: requestBody =>
    !!requestBody &&
    postData({
      endpoint: endpoint?.auth?.registerToken,
      body: requestBody,
    }),
  removeToken: requestBody =>
    !!requestBody &&
    postData({
      endpoint: endpoint?.auth?.removeToken,
      body: requestBody,
    }),
  sendActivation: requestBody =>
    !!requestBody &&
    postData({
      endpoint: endpoint?.auth?.sendActivation,
      body: requestBody,
    }),
};

// Redis Cache
const cache = {
  totalDelete: () => patchData({
    endpoint: endpoint.cache?.totalDelete,
    body: ""
  })
};

// Users
const users = {
  getByID: id => !!id && getData({
    endpoint: endpoint.users?.single(id)
  }),
  getByIDBySchool: id => !!id && getData({
    endpoint: endpoint.users?.singleForSchool(id)
  }),
  getParentChildren: parentID => !!parentID && getData({
    endpoint: endpoint.parent.childrenList(parentID)
  }),
  createUser: data => !!data && postData({
    endpoint: endpoint.users?.new,
    body: data
  }),
  linkStudent: data => !!data && postData({
    endpoint: endpoint.users?.linkStudent,
    body: data
  }),
  update: (data, action = 'update') => !!data && postData({
    endpoint: endpoint.users?.update,
    body: {
      user: data,
      actionType: action
    }
  }),
}

// Notifications
const notifications = {
  // for mobile: paretn and teacher
  parent: {
    getChildNotifications: (userId, childID) => (!!userId && !!childID) && getData({
      endpoint: endpoint.notifications.parent.notRead(userId, childID),
    }),
    getChildrenNotifications: (userId) => !!userId && getData({
      endpoint: endpoint.notifications.parent.notReadAll(userId),
    }),
  },
  teacher: {
    getSchoolNotifications: (userId, schoolKey) => (!!userId && !!schoolKey) && getData({
      endpoint: endpoint.notifications.teacher.notRead(userId, schoolKey),
    }),
    getSchoolsNotifications: (userId) => !!userId && getData({
      endpoint: endpoint.notifications.teacher.notReadAll(userId),
    }),
  },
};
// Global routes
const route = {
  fetchAll: (endpoint, endponitSend) => !!endpoint && getData({
    endpoint: endpoint,
    body: {
      params: endponitSend,
    },
  }),
  findAll: (endpoint, endponitSend) => !!endpoint && postData({
    endpoint: endpoint,
    body: {
      params: endponitSend,
    },
  }),
};
// Media (upload files)
const media = {
  upload: file => !!file && postFormData({
    endpoint: endpoint?.media?.upload,
    file: file,
  }),
  delete: fileUrl => !!fileUrl && patchData({
    endpoint: endpoint?.media?.delete,
    body: {
      url: fileUrl,
    },
  }),
};
// Absences
const absence = {
  updateAbsence: absence => !!absence && patchData({
    endpoint: endpoint?.absences?.update,
    body: absence,
  }),
};
// Only for students
const students = {
  exportStudents: studentsIDs => !!studentsIDs && patchData({
    endpoint: endpoint?.students?.export,
    body: {
      students: studentsIDs,
    },
  }),
  validateToken: registerToken => !!registerToken && patchData({
    endpoint: endpoint?.students?.validation,
    body: {
      registerToken: registerToken,
    },
  }),
};

// Communications
const communications = {
  fetchAllTypes: () => getData({
    endpoint: endpoint?.communications?.types
  }),
  remindCommunication: commId => !!commId && postData({
      endpoint: endpoint.communications.remind(commId),
    }),
  createAudienceFromComm: (commId, audienceObj = { title: "New Audience" }) => !!commId && postData({
    endpoint: endpoint.communications.audiences.new(commId),
    body: audienceObj,
  }),
  getCommAudiencesById: commId => !!commId && getData({
    endpoint: endpoint.communications.audiences.single(commId),
    body: null,
  }),
  forceUpdateCommAudiencesAndSentById: commId => !!commId && postData({
    endpoint: endpoint.communications.audiences.force(commId),
    body: {commId},
  }),
  updateCommAudiences: (
    commId,
    commObj = {
      deletedIds: {
        students: [],
        users: [],
      },
      addedIds: {
        students: [],
        users: [],
      },
    },
  ) => !!commId && postData({
    endpoint: endpoint.communications.audiences.update(commId),
    body: commObj,
  }),
  markAs: (commId, type = null) => !!commId && postData({
    endpoint: endpoint.users.communications.markAs(commId),
    body: { type },
  }),
};

// Discussions for communications
const discussions = {
  getDiscussionById: (discussionId) => !!discussionId && getData({
    endpoint: endpoint?.discussions?.single(discussionId),
  }),
  getDiscussionMessagesById: ({ communicationId, ...rest }) => !!communicationId && postData({
    endpoint: endpoint?.discussions?.messages?.list(communicationId),
    body: rest,
  }),
  // messages
  sendMessage: (data) => !!data && postData({
    endpoint: endpoint?.discussions?.messages?.new,
    body: data
  }),
  uploadVoiceMessage: (data) => !!data && postData({
    endpoint: endpoint?.media?.uploadAudio,
    body: data,
  }, {headers: { "Content-Type": "multipart/form-data" }}),
  markAsRead: (discussionId) => {
    const localUser = _local.getLocalUser();
    const localChild = _local.getLocalChild();
    const body = {
      student: localUser?.role == "parent" ? localChild?._id : null,
    };
    return (!!discussionId && postData({
      endpoint: endpoint?.discussions?.readersMarks(discussionId),
      body
    }));
  },
};

const api = {
  auth,
  cache,
  users,
  notifications,
  route,
  media,
  absence,
  students,
  communications,
  discussions,
  // functions
  setAuthorizationHeader,
  postData,
  postFormData,
  getData,
  patchData,
  // endpoints
  endpoint,
};
export default api;
